<template>
    <div>
      <div class="d-flex flex-column flex-lg-row align-items-start">
        <div class="d-flex flex-column" :class="['w-100', 'mb-4', 'mb-md-0', 'me-md-4']">
          <div class="main-image-container">
            <img
              class="img-fluid object-fit-cover rounded-3"
              style="height: 500px;"
              alt="main"
              :src="activeImage"
            />
          </div>
  
          <div class="thumbnail-gallery mt-3 d-flex gap-2 overflow-x-auto">
            <img
              v-for="(image, index) in activeBox?.images || []"
              :key="index"
              class="thumbnail rounded-2"
              :src="image.src"
              :alt="'Thumbnail ' + index"
              :class="{ active: activeImage === image.src }"
              style="width: 120px; height: 120px; min-width: 120px; cursor: pointer; object-fit: cover;"
              @click="setActiveImage(image.src)"
            />
          </div>
  
          <div class="product-tabs mt-4 d-none d-lg-block">
            <div class="nav nav-tabs d-flex align-items-center overflow-x-auto" style="min-height: 43px;">
              <div class="nav-item" v-for="(tab, index) in tabs" :key="index">
                <a
                  class="nav-link"
                  :class="{ active: activeTab === tab.key }"
                  href="#"
                  @click.prevent="activeTab = tab.key"
                >
                  {{ tab.label }}
                </a>
              </div>
            </div>
            <div class="tab-content mt-4">
              <div v-if="activeTab === 'description'" class="tab-pane active">
                <h3>BESCHRIJVING</h3>
                <div v-html="activeBox?.description || ''"></div>
              </div>
              <div v-else-if="activeTab === 'extraInfo'" class="tab-pane active">
                <h3>EXTRA INFORMATIE</h3>
                <div class="allergen-info">
                  <p v-html="activeBox?.acf_fields?.extra_informatie"></p>
                </div>
              </div>
              <div v-else-if="activeTab === 'allergens'" class="tab-pane active">
                <h3>ALLERGENEN</h3>
                <div class="allergen-info">
                  <p v-html="activeBox?.acf_fields?.allergenen"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <div class="box-info w-100 ms-0 ms-lg-4">
          <h1 class="fw-bold" style="color: #000; font-size: 38px !important;">{{ activeBox?.name || '' }}</h1>
          <p class="mt-3 text-red" style="font-size: 20px; font-weight: 600;">
            €{{ formatPrice(getMinPrice()) }} - €{{ formatPrice(getMaxPrice()) }}
          </p>
          <div class="short-description mt-3" v-html="activeBox?.short_description || ''"></div>
          
          <div class="d-flex align-items-center flex-wrap mt-4" v-if="activeBox?.product_options?.smaken" style="gap: 10px !important;">
            <div
              class="bg-white d-flex align-items-center justify-content-center flavor-container"
              style="border-radius: 200px; width: 80px; height: 80px; position: relative;"
              v-for="flavor in activeBox.product_options.smaken"
              :key="flavor.id"
              @mouseenter="showTooltip(flavor.id)"
              @mouseleave="hideTooltip"
            >
              <img alt="flavor" :src="flavor.image" width="62" />
              <div
                class="flavor-tooltip"
                :class="{ 'show-tooltip': activeTooltip === flavor.id }"
                role="tooltip"
              >
                {{ flavor.name }}
              </div>
            </div>
          </div>
  
          <div class="multi-step-form mt-3">
            <div class="form-steps-container">
              <transition name="slide-fade" mode="out-in">
                <div v-if="currentStep === 1" class="d-flex flex-column" key="step1">
                  <div class="size-selector mt-4">
                    <h4
                      :style="testProductDetail ? 'font-size: 20px !important;' : 'font-size: 26px;'"
                      class="me-2 mb-2 mb-md-0"
                    >
                      <span class="text-red">{{ testProductDetail ? 'Stap 1/3' : 'Stap 4/4' }}</span> - KIES AANTAL STUKS
                    </h4>
                    <select
                      v-if="activeBox?.product_options?.size_options?.length"
                      id="sizeSelect"
                      class="form-select mt-2"
                      style="height: 50px"
                      v-model="selectedSize"
                      @change="updatePrice"
                    >
                      <option
                        v-for="(size, index) in activeBox.product_options.size_options"
                        :key="index"
                        :value="size"
                      >
                        {{ size.size }} stuks - €{{ formatPrice(size.price) }}
                      </option>
                    </select>
                  </div>
                  <button 
                    class="btn btn-primary mt-4 rounded-pill bg-red border-0 px-4 py-2 align-self-end"
                    @click="nextStep"
                  >
                    Volgende
                  </button>
                </div>
  
                <div v-else-if="currentStep === 2" class="step-content" key="step2">
                  <cart-picker
                    @pickdate="handlePickDate"
                    :boxdata="boxdata"
                    @cardtext="handleCardText"
                    @cardname="handleCardName"
                    @pickcard="handlePickCard"
                    :testProductDetail="testProductDetail"
                  />
                  <div class="d-flex justify-content-between mt-4">
                    <button class="btn btn-secondary rounded-pill bg-red border-0 px-4 py-2" @click="prevStep">
                      Vorige
                    </button>
                    <button class="btn btn-primary rounded-pill bg-red border-0 px-4 py-2" @click="nextStep">
                      Volgende
                    </button>
                  </div>
                </div>
  
                <div v-else-if="currentStep === 3" class="step-content" key="step3">
                  <where-to
                    :boxdata="boxdata"
                    @addresschange="handleAddressChange"
                    @another-box="handleAnotherBox"
                    :testProductDetail="testProductDetail"
                    :activeBox="activeBox"
                  />
                  <div class="d-flex justify-content-between mt-4">
                    <button class="btn btn-secondary rounded-pill bg-red border-0 px-4 py-2" @click="prevStep">
                      Vorige
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
  
      <div class="product-tabs mt-3 d-flex flex-column d-lg-none">
        <ul class="nav nav-tabs overflow-x-auto" style="min-height: 43px;">
          <li class="nav-item" v-for="(tab, index) in tabs" :key="index">
            <a
              class="nav-link"
              :class="{ active: activeTab === tab.key }"
              href="#"
              @click.prevent="activeTab = tab.key"
            >
              {{ tab.label }}
            </a>
          </li>
        </ul>
        <div class="tab-content mt-4">
          <div v-if="activeTab === 'description'" class="tab-pane active">
            <h3>BESCHRIJVING</h3>
            <div v-html="activeBox?.description || ''"></div>
          </div>
          <div v-else-if="activeTab === 'extraInfo'" class="tab-pane active">
            <h3>EXTRA INFORMATIE</h3>
            <ul>
              <li v-for="attribute in activeBox?.attributes || []" :key="attribute.id">
                <strong>{{ attribute.name }}:</strong> {{ attribute.options.join(", ") }}
              </li>
            </ul>
          </div>
          <div v-else-if="activeTab === 'allergens'" class="tab-pane active">
            <h3>ALLERGENEN</h3>
            <div class="allergen-info">
              <p v-html="activeBox?.acf_fields?.allergenen"></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, defineProps, defineEmits, watch } from "vue";
  import CartPicker from "../cart-picker.vue";
  import WhereTo from "../where-to.vue";
  
  const emit = defineEmits([
    'pickdate',
    'cardtext',
    'cardname',
    'pickcard',
    'addresschange',
    'another-box',
    'size-change'
  ]);
  
  const props = defineProps({
    boxes: {
      type: Array,
      required: true,
    },
    boxdata: {
      type: Object,
      required: true,
    },
    testProductDetail: {
      type: Boolean,
      default: false,
    },
    activeBox: {
      type: Object,
      default: () => ({})
    }
  });
  
  const activeImage = ref("");
  const activeTab = ref('description');
  const currentStep = ref(1);
  const selectedSize = ref(null);
  const activeTooltip = ref(null);
  
  watch(() => props.activeBox, (newBox) => {
    if (newBox?.images?.[0]?.src) {
      activeImage.value = newBox.images[0].src;
    }
    if (newBox?.product_options?.size_options?.length > 0) {
      selectedSize.value = newBox.product_options.size_options[0];
    }
  }, { immediate: true });
  
  const tabs = [
    { key: "description", label: "BESCHRIJVING" },
    { key: "extraInfo", label: "EXTRA INFORMATIE" },
    { key: "allergens", label: "ALLERGENEN" },
  ];
  
  const handlePickDate = (event) => {
    emit('pickdate', event);
  };
  
  const handleCardText = (event) => {
    emit('cardtext', event);
  };
  
  const handleCardName = (event) => {
    emit('cardname', event);
  };
  
  const handlePickCard = (event) => {
    emit('pickcard', event);
  };
  
  const handleAddressChange = (event) => {
    emit('addresschange', event);
  };
  
  const handleAnotherBox = (event) => {
    emit('another-box', event);
  };
  
  const formatPrice = (price) => {
    const numPrice = Number(price);
    return isNaN(numPrice) ? '0.00' : numPrice.toFixed(2);
  };
  
  const getMinPrice = () => {
    const sizeOptions = props.activeBox?.product_options?.size_options || [];
    if (!sizeOptions.length) return 0;
    const prices = sizeOptions.map(option => Number(option.price)).filter(price => !isNaN(price));
    return prices.length ? Math.min(...prices) : 0;
  };
  
  const getMaxPrice = () => {
    const sizeOptions = props.activeBox?.product_options?.size_options || [];
    if (!sizeOptions.length) return 0;
    const prices = sizeOptions.map(option => Number(option.price)).filter(price => !isNaN(price));
    return prices.length ? Math.max(...prices) : 0;
  };
  
  const setActiveImage = (imageSrc) => {
    activeImage.value = imageSrc;
  };
  
  const updatePrice = () => {
    emit('size-change', selectedSize.value);
  };
  
  const nextStep = () => {
    if (currentStep.value < 3) {
      currentStep.value += 1;
    }
  };
  
  const prevStep = () => {
    if (currentStep.value > 1) {
      currentStep.value -= 1;
    }
  };
  
  const showTooltip = (flavorId) => {
    activeTooltip.value = flavorId;
  };
  
  const hideTooltip = () => {
    activeTooltip.value = null;
  };
  </script>
  
  <style scoped>
  .form-steps-container {
    position: relative;
    min-height: 200px;
  }
  
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: all 0.3s ease;
  }
  
  .slide-fade-enter-from {
    opacity: 0;
    transform: translateX(0px);
  }
  
  .slide-fade-leave-to {
    opacity: 0;
    transform: translateX(0px);
  }
  
  .step-content {
    width: 100%;
  }
  
  .nav-tabs {
    border: none;
    flex-wrap: unset;
    overflow-y: hidden !important;
  }

  .nav-item {
    min-width: fit-content;
  }
  
  .nav-link {
    cursor: pointer;
    font-size: 15px;
    padding: 9px 20px 7px 20px !important;
    font-weight: bold;
    border-radius: 100px;
    transition: all 0.3s ease;
    border: 1px solid transparent;
    margin-right: 15px;
    border: 1px solid #152e88;
    color: #152e88;
  }

  .nav-link {
    height: 40px !important;
    display: flex;
    align-items: center;
    font-size: 16px !important;
    font-family: 'Anton-Regular';
  }
  
  .nav-link:hover {
    border: 1px solid transparent;
    background-color: #152e88;
    color: white;
  }
  
  .nav-link.active {
    background-color: #152e88;
    color: white;
    border: 1px solid #152e88;
  }
  
  .main-image-container img {
    width: 100%;
    height: 500px;
    object-fit: cover !important;
    transition: all 0.3s ease;
  }
  
  .thumbnail-gallery {
    padding-bottom: 10px;
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  }
  
  .thumbnail-gallery::-webkit-scrollbar {
    height: 6px;
  }
  
  .thumbnail-gallery::-webkit-scrollbar-track {
    background: transparent;
  }
  
  .thumbnail-gallery::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  
  .thumbnail {
    transition: opacity 0.3s ease, border-color 0.3s ease;
  }
  
  .thumbnail:hover {
    opacity: 0.8;
  }
  
  .thumbnail.active {
    border: 2px solid #0b5ed7;
    opacity: 1;
  }
  
  .flavor-container {
    border-radius: 200px;
    width: 80px;
    height: 80px;
    position: relative;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  
  .flavor-container:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .flavor-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease, transform 0.2s ease;
  z-index: 1000;
  margin-bottom: 8px;
  transform: translateX(-50%) translateY(5px);
}

.flavor-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 5px;
  border-style: solid;
  border-color: rgba(0, 0, 0, 0.85) transparent transparent transparent;
}

.flavor-tooltip.show-tooltip {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

/* Form styles */
.form-select {
  border-radius: 8px;
  border-color: #dee2e6;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-select:focus {
  border-color: #0b5ed7;
  box-shadow: 0 0 0 0.25rem rgba(11, 94, 215, 0.25);
}

/* Button styles */
.btn {
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

/* Responsive styles */
@media (max-width: 767.98px) {
  .main-image-container img {
    height: 300px;
  }

  .thumbnail-gallery img {
    width: 80px;
    height: 80px;
  }

  .me-md-4 {
    margin-right: 0 !important;
  }
}

@media (max-width: 678px) {
  .main-image-container img {
    max-height: 300px;
  }

  .form-steps-container {
    min-height: 250px;
  }
}

::v-deep h3 {
  font-size: 24px !important;
  padding-bottom: 0px !important;
}

::v-deep p {
  margin: 10px 0;
}

::v-deep ol {
  list-style: decimal;
  margin-bottom: 30px;
}

::v-deep ol li {
  margin-bottom: 15px;
  line-height: 22px;
}
</style>